import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Loader2, Trash2, CalendarClock } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  listReportSubscriptions,
  updateReportSubscription,
  deleteReportSubscription,
  type ReportSubscription,
} from '@/lib/reportSubscriptionsApi';

interface ReportSubscriptionsManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bump to force a refetch (e.g. after a new subscription is created). */
  refreshKey?: number;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TZ_SHORT: Record<string, string> = {
  'America/Los_Angeles': 'PT', 'America/Denver': 'MT', 'America/Chicago': 'CT', 'America/New_York': 'ET',
};

function scheduleSummary(s: ReportSubscription): string {
  const hour = ((s.send_hour + 11) % 12) + 1;
  // Include the zone — two subs at the same hour in different zones must read differently.
  const tz = TZ_SHORT[s.timezone] ?? s.timezone;
  const at = `${hour}:00 ${s.send_hour < 12 ? 'AM' : 'PM'} ${tz}`;
  if (s.cadence === 'daily') return `Daily · ${at}`;
  if (s.cadence === 'weekly') return `Weekly · ${DAYS[s.day_of_week ?? 0]} · ${at}`;
  return `Monthly · day ${s.day_of_month} · ${at}`;
}

function nextSend(s: ReportSubscription): string {
  if (!s.enabled) return 'Paused';
  if (!s.next_due_at) return '—';
  return new Date(s.next_due_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function ReportSubscriptionsManager({ open, onOpenChange, refreshKey = 0 }: ReportSubscriptionsManagerProps) {
  const [subs, setSubs] = useState<ReportSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    // "Latest wins": a refreshKey change while a fetch is in flight must not let the
    // older response overwrite the newer one.
    let ignore = false;
    setLoading(true);
    (async () => {
      try {
        const { subscriptions } = await listReportSubscriptions();
        if (!ignore) setSubs(subscriptions);
      } catch (err) {
        if (!ignore) toast.error(err instanceof Error ? err.message : 'Could not load your subscriptions.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [open, refreshKey]);

  const togglePause = async (s: ReportSubscription) => {
    setBusyId(s.id);
    try {
      await updateReportSubscription(s.id, { enabled: !s.enabled });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, enabled: !x.enabled } : x)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the subscription.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (s: ReportSubscription) => {
    if (!window.confirm(`Delete "${s.name}"? Recipients will stop receiving it.`)) return;
    setBusyId(s.id);
    try {
      await deleteReportSubscription(s.id);
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
      toast.success('Subscription deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the subscription.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>My report subscriptions</SheetTitle>
          <SheetDescription>Scheduled reports you own. Pause to stop sending without deleting.</SheetDescription>
        </SheetHeader>

        <div className="py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : subs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
              <CalendarClock className="h-8 w-8 opacity-40" />
              <p className="text-sm">No subscriptions yet.</p>
              <p className="text-xs">Use “Subscribe” on a report to schedule it.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {subs.map((s) => (
                <li key={s.id} className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{s.name}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{scheduleSummary(s)}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">Next: {nextSend(s)}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Switch checked={s.enabled} disabled={busyId === s.id} onCheckedChange={() => togglePause(s)} title={s.enabled ? 'Pause' : 'Resume'} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" disabled={busyId === s.id} onClick={() => remove(s)} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
